import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, Mail, Phone, MessageCircle, Globe } from 'lucide-react';

import { OFFER } from '../constants/demos';

const SUPPORT_EMAIL = 'support@globalnow.biz';
const SUPPORT_PHONE = OFFER.phoneDisplay;
const SUPPORT_PHONE_TEL = OFFER.phoneTel;

const Footer = () => {
  return (
    <footer className="bg-gray-900/80 border-t border-gray-800 mt-20">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-bold">globalnow.biz</span>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed">
              We build your business website for ${OFFER.price} one-time. No monthly fees.
            </p>
            <div className="mt-4 space-y-2">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
              >
                <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                {SUPPORT_EMAIL}
              </a>
              <a
                href={`tel:${SUPPORT_PHONE_TEL}`}
                className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
              >
                <Phone className="w-4 h-4 text-green-400 shrink-0" />
                {SUPPORT_PHONE} — 24/7
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Product</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/order"             className="text-gray-400 hover:text-white transition-colors">Order a Website — ${OFFER.price}</Link></li>
              <li><a href="/blog/"              className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
              <li><Link to="/examples"          className="text-gray-400 hover:text-white transition-colors">Live Examples</Link></li>
              <li><Link to="/features"          className="text-gray-400 hover:text-white transition-colors">Self-service tools (separate product)</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/order"           className="text-gray-400 hover:text-white transition-colors">Website pricing — ${OFFER.price}</Link></li>
              <li>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-400 hover:text-white transition-colors">
                  Contact Us
                </a>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
              </li>
              <li>
                <Link to="/refund" className="text-gray-400 hover:text-white transition-colors">Refund Policy</Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Support</h4>
            <ul className="space-y-2 text-sm">
              <li className="text-gray-400">
                <span className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-blue-400 shrink-0" />
                  Mr. Support AI — bottom left corner, always available
                </span>
              </li>
              <li>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <Mail className="w-4 h-4 shrink-0" />
                  Email support
                </a>
              </li>
              <li>
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <Phone className="w-4 h-4 shrink-0" />
                  Call us: {SUPPORT_PHONE}
                </a>
              </li>
            </ul>

            <div className="mt-6 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-300">
                💬 Fastest answer: ask <strong>Mr. Support</strong> — the AI chatbot on every page responds instantly, 24/7.
              </p>
            </div>
          </div>
        </div>

        {/* Local pages — plain <a>, not <Link>: these are static HTML pages, not React routes */}
        <div className="mt-10 pt-6 border-t border-gray-800 text-center">
          <p className="text-gray-600 text-xs mb-2">Serving local businesses in:</p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <a href="/cities/orlando-fl/" className="hover:text-gray-300 transition-colors">Orlando, FL</a>
            <a href="/cities/atlanta-ga/" className="hover:text-gray-300 transition-colors">Atlanta, GA</a>
            <a href="/cities/miami-fl/" className="hover:text-gray-300 transition-colors">Miami, FL</a>
            <a href="/cities/new-york-ny/" className="hover:text-gray-300 transition-colors">New York, NY</a>
            <a href="/cities/los-angeles-ca/" className="hover:text-gray-300 transition-colors">Los Angeles, CA</a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-600 text-xs">
            © {new Date().getFullYear()} globalnow.biz — All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <span>🔒 Done-for-you website checkout via Payhip</span>
            <span>•</span>
            <span>🛡️ 30-day satisfaction guarantee</span>
            <span>•</span>
            <span>💎 One-time payment, lifetime access</span>
          </div>
        </div>
        <div className="mt-4 text-center">
          <p className="text-gray-600 text-xs">
            Built and operated by an AI agent fleet —{' '}
            <a
              href="https://aisuperhub.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-300 underline transition-colors"
            >
              powered by AI SuperHub
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
